// filepath: apps/web/src/features/admin/hmacKeys/RotateKeyDialog.tsx
//
// FR-UI-ADM-7 AC-3/AC-5 -- grace-window configurator + rotation submitter.
//
// The dialog renders:
//   - 1–72 hour slider (snaps to whole hours)
//   - Submit button -> POST /api/webhooks/keys/rotate with X-Idempotency-Key
//   - On 200, the parent (HmacKeysPage) opens the OneTimeCopyDialog
//   - On 400 with invalid_grace_window, surfaces an inline error
//
// NOTE: client-side bounds [60, 4320] minutes exactly mirror the
// server-side bounds. The server-side check is the source of truth.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRotateKey } from "./queries";
import { errorApiToMessage } from "./errorMessage";
import type { RotateKeyResponse } from "./types";

interface RotateKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (resp: RotateKeyResponse) => void;
  graceWindowMinutes: number;
  onGraceWindowChange: (minutes: number) => void;
}

const MIN_MINUTES = 60; // 1 hour
const MAX_MINUTES = 4320; // 72 hours
const DEFAULT_MINUTES = 1440; // 24 hours

export function RotateKeyDialog({
  open,
  onClose,
  onSuccess,
  graceWindowMinutes,
  onGraceWindowChange,
}: RotateKeyDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const rotate = useRotateKey();
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const hours = Math.floor(graceWindowMinutes / 60);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const resp = await rotate.mutateAsync({
        grace_window_minutes: graceWindowMinutes,
      });
      onSuccess(resp);
    } catch (err) {
      setError(errorApiToMessage(err, t));
    }
  };

  const resetToDefault = () => onGraceWindowChange(DEFAULT_MINUTES);

  return (
    <div
      data-testid="rotate-key-dialog"
      className="hmac-keys-dialog"
      role="dialog"
      aria-modal="true"
    >
      <h2>{t("admin.webhooks.keys.rotate_action")}</h2>
      <form onSubmit={handleSubmit}>
        <label htmlFor="grace-window-slider">
          {t("admin.webhooks.keys.grace_window_label")}:{" "}
          {t("admin.webhooks.keys.grace_window_hours", { hours })}
        </label>
        <input
          id="grace-window-slider"
          type="range"
          min={1}
          max={72}
          step={1}
          value={hours}
          onChange={(e) => {
            const h = Number(e.target.value);
            onGraceWindowChange(h * 60);
          }}
          data-testid="grace-window-slider"
        />
        <p>
          <small>
            {t("admin.webhooks.keys.grace_window_minutes_min", {
              minutes: MIN_MINUTES,
            })}{" "}
            —{" "}
            {t("admin.webhooks.keys.grace_window_minutes_max", {
              minutes: MAX_MINUTES,
            })}
          </small>
        </p>
        <button
          type="button"
          onClick={resetToDefault}
          data-testid="grace-window-reset-default"
        >
          {DEFAULT_MINUTES / 60}h
        </button>

        {error && (
          <p data-testid="rotate-key-error" className="hmac-keys-error">
            {error}
          </p>
        )}

        <div className="hmac-keys-dialog__actions">
          <button
            type="button"
            onClick={onClose}
            data-testid="rotate-key-cancel"
          >
            {t("admin.webhooks.keys.cancel_button")}
          </button>
          <button
            type="submit"
            disabled={rotate.isPending}
            data-testid="rotate-key-submit"
          >
            {rotate.isPending ? "..." : t("admin.webhooks.keys.rotate_action")}
          </button>
        </div>
      </form>
    </div>
  );
}
